import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Check, Shield, Clock, FileText } from "lucide-react";

export default function LoansPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", loan_amount: "", tenure_months: "60", monthly_income: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await api.post("/leads/loan", { ...form, loan_amount: form.loan_amount ? parseInt(form.loan_amount) : null, tenure_months: parseInt(form.tenure_months), monthly_income: form.monthly_income ? parseInt(form.monthly_income) : null });
      toast.success("Loan enquiry submitted!"); setDone(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="cs-container py-20 max-w-xl text-center">
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 text-emerald-600 mx-auto"><Check size={30} /></div>
      <h1 className="mt-5 font-display font-extrabold text-3xl text-gray-900">Enquiry received</h1>
      <p className="mt-3 text-gray-600">Our finance team will connect with you to discuss loan options.</p>
    </div>
  );

  return (
    <div className="cs-container py-10" data-testid="loans-page">
      <div className="max-w-2xl">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-gray-900">Used Car Finance</h1>
        <p className="mt-3 text-gray-600">Flexible loan options for your pre-owned car purchase. Our team will help you with the paperwork and connect you with the right financing partner.</p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        {[{ icon: Shield, t: "Trusted Partners", d: "We work with established banks and NBFCs." },
          { icon: Clock, t: "Quick Processing", d: "Hassle-free document pickup and fast disbursement." },
          { icon: FileText, t: "Minimal Paperwork", d: "Our team handles the end-to-end documentation." }].map((f, i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-5">
            <f.icon className="text-[#940C2C]" size={22} />
            <div className="mt-3 font-display font-bold">{f.t}</div>
            <p className="mt-1 text-sm text-gray-500">{f.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">Loan Enquiry</h2>
          <p className="text-sm text-gray-500 mt-1">Share your details — our team will reach out to discuss tenure, interest rate and eligibility.</p>
          <form onSubmit={submit} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-gray-200 rounded-2xl p-6 bg-white">
            <Input label="Your Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="loan-name" />
            <Input label="Phone" required type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="loan-phone" />
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Input label="Loan Amount (₹)" type="number" value={form.loan_amount} onChange={(v) => setForm({ ...form, loan_amount: v })} />
            <div>
              <label className="text-xs font-semibold text-gray-700">Tenure</label>
              <select value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="24">24 months</option><option value="36">36 months</option><option value="48">48 months</option><option value="60">60 months</option><option value="72">72 months</option>
              </select>
            </div>
            <Input label="Monthly Income (₹)" type="number" value={form.monthly_income} onChange={(v) => setForm({ ...form, monthly_income: v })} />
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-gray-700">Message</label>
              <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary sm:col-span-2" data-testid="loan-submit">
              {submitting ? "Submitting…" : "Submit Loan Enquiry"}
            </button>
          </form>
        </div>
        <div className="rounded-2xl overflow-hidden h-fit">
          <img src="https://images.unsplash.com/photo-1618642624018-a370cbf3cd80?w=1200" alt="" className="w-full h-72 object-cover" />
          <div className="bg-gray-50 border border-gray-100 p-6 rounded-b-2xl">
            <h3 className="font-display font-bold">What you may need</h3>
            <ul className="mt-3 space-y-2 text-sm text-gray-700">
              <li>• Government-issued photo ID & address proof</li>
              <li>• Recent salary slips or ITR for self-employed</li>
              <li>• 3–6 months bank statement</li>
              <li>• Passport-size photographs</li>
            </ul>
            <p className="mt-4 text-xs text-gray-500">Final eligibility, interest rate and documentation depend on your financing partner.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Input = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="text-xs font-semibold text-gray-700">{label}{required && " *"}</label>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-[#940C2C]" data-testid={testid} />
  </div>
);
